import { useEffect } from 'react';
import { BaseProperty, InternalProperty } from '@/features/property/types';
import { RealtimeChartDataResponse } from '../api/fetchRealtimeProperty';
import { UseQueryResult } from '@tanstack/react-query';
import { Competitor } from '@/features/compset/types';
import { ChartDataResponse } from '@/features/charts/api/fetchChartData';
import { ChartProperty } from '@/features/charts/types';

interface PropertyDataState {
  isLoading: boolean;
  isError: boolean;
  chartProperty: ChartProperty | null;
  competitors: Competitor[];
  marketProperties: BaseProperty[];
  hasNoData: boolean;
}

export const usePropertyDataState = (
  propertyResponse: UseQueryResult<BaseProperty | InternalProperty, Error>,
  propertyChartResponse: UseQueryResult<RealtimeChartDataResponse | ChartDataResponse, Error>,
  toggleAllProperties: (
    isVisible: boolean,
    properties: BaseProperty[],
    excludeId?: number
  ) => void
): PropertyDataState => {
  const isLoading = propertyChartResponse.isLoading || propertyResponse.isLoading;
  const isError = propertyChartResponse.isError || propertyResponse.isError;

  const competitors = propertyChartResponse.data?.competitors ?? [];
  const marketProperties = propertyChartResponse.data?.market_properties ?? [];
  const chartProperty = propertyChartResponse.data?.property ?? null;

  const hasNoData = !propertyChartResponse.isLoading &&
    propertyChartResponse.data &&
    !chartProperty;

  // Common effect for toggling properties
  useEffect(() => {
    if (competitors.length > 0 && propertyResponse.data) {
      toggleAllProperties(
        true,
        [propertyResponse.data, ...competitors],
        propertyResponse.data.property_id
      );
    }
  }, [competitors, propertyResponse.data, toggleAllProperties]);

  return {
    isLoading,
    isError,
    chartProperty,
    competitors,
    marketProperties,
    hasNoData
  };
};